/**
 * Infinite scrolling web component.
 *
 * Requires:
 *  - .js-pagination
 *  - .js-pagination__item
 *  - .js-pagination__next-button[href]
 *
 * Universal sorting:
 *  - Product card outer wrapper must include data-universal="true|false"
 *  - Universal items are buffered and removed until the last page, then appended at the end.
 */

if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {

    /* DEBUG CODE - REMOVE IF NOT NEEDED */
    _dbgOn() {
      try {
        if (!window.__INFDBG?.enabled) return false;
        const only = window.__INFDBG?.onlyPaths;
        if (Array.isArray(only) && only.length) {
          return only.some(p => window.location.pathname.includes(p));
        }
        return true;
      } catch (e) {
        return true;
      }
    }

    _dbg(label, data = {}) {
      if (!this._dbgOn()) return;

      const items = this.querySelectorAll('.js-pagination__item').length;
      const universals = this.querySelectorAll('.js-pagination__item [data-universal="true"]').length;
      const hasNext = (() => { try { return this.hasNextPage(); } catch { return 'ERR'; } })();

      console.log(`[InfiniteScroll][${label}]`, {
        path: window.location.pathname,
        items,
        universals,
        buffer: this._universalBuffer?.length ?? null,
        done: this._universalDone ?? null,
        hasNext,
        nextHref: this.nextButton?.getAttribute('href') || this.nextButton?.href || null,
        ...data,
      });
    }
    /* END OF DEBUG CODE - REMOVE IF NOT NEEDED */

    constructor() {
      super();

      // Instance-scoped universal state (prevents cross-grid interference)
      this._universalBuffer = [];
      this._universalDone = false;
      this._lastFetchedHref = null;
      this._suspendGridObserver = false;

      // Timers / observers
      this._gridObserver = null;
      this._gridChangeTimer = null;

      // Bind once so removeEventListener works reliably
      this.clickHandler = this.onNextButtonClick.bind(this);

      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    /**
     * ✅ PROMO PINNING:
     * Keeps <li class="collection-inline-promo"> as the 3rd visible item
     * after universal buffering/hiding changes the visible list.
     */
    pinInlinePromoToThird() {
      const grid = this.querySelector('#product-grid');
      if (!grid) return;

      const promo = grid.querySelector('li.collection-inline-promo');
      if (!promo) return;

      // Prevent promo move from triggering the grid MutationObserver reset
      this._suspendGridObserver = true;

      // visible items = everything except promo + hidden universals
      const visible = [...grid.children]
        .filter(el => el !== promo)
        .filter(el => !el.classList.contains('universal-buffered'));

      const beforeNode = visible[2] || null; // 3rd visible card

      if (beforeNode) grid.insertBefore(promo, beforeNode);
      else grid.appendChild(promo);

      // release on next frame so normal rerenders still work
      requestAnimationFrame(() => {
        this._suspendGridObserver = false;
      });
    }


    init() {
      // Refresh references
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');

      if (!this.paginationItems.length) return;

      // Reset INSTANCE state per grid render
      this._universalBuffer = [];
      this._universalDone = false;
      this._lastFetchedHref = null;

      // Watch for AJAX/facets re-renders (debounced)
      this.observeGridChanges();

      // Pass 1: immediate
      this.splitUniversalWithinContainer();
      this.pinInlinePromoToThird(); // ✅ pin after pass 1

      // Pass 2: next frame (stabilizes timing / late DOM inserts)
      requestAnimationFrame(() => {
        this.splitUniversalWithinContainer();
        this.pinInlinePromoToThird(); // ✅ pin after pass 2

        // If no next page, we are already at the end → flush now
        if (!this.hasNextPage()) {
          this.flushUniversalBuffer();
          this.pinInlinePromoToThird(); // ✅ pin after flush
        }
      });

      // Only bind infinite scroll / show more if there is a real next page
      if (this.hasNextPage()) {
        this.bindListeners();
      }
    }

    observeGridChanges() {
      if (this._gridObserver) this._gridObserver.disconnect();

      // Observe the actual grid container if possible (best), otherwise fallback to this element
      const grid =
        this.querySelector('#product-grid') ||
        this.querySelector('.product-grid');

      const target = grid || this;

      this._gridObserver = new MutationObserver((mutations) => {
        // ✅ Ignore mutations caused by infinite scroll inserting items
        if (this._suspendGridObserver || this.fetchInProgress) return;

        // Only react if nodes added/removed look like a re-render
        const significant = mutations.some(m => {
          if (m.type !== 'childList') return false;
          if (m.removedNodes && m.removedNodes.length) return true;
          if (m.addedNodes && m.addedNodes.length) {
            return [...m.addedNodes].some(n =>
              n.nodeType === 1 &&
              (n.matches?.('#product-grid, .product-grid') || n.querySelector?.('.js-pagination__item'))
            );
          }
          return false;
        });

        if (!significant) return;

        clearTimeout(this._gridChangeTimer);

        // Debounce - theme often does multiple DOM ops during a rerender
        this._gridChangeTimer = setTimeout(() => {
          // refresh references (DOM likely replaced)
          this.paginationContainer = this.querySelector('.js-pagination');
          this.paginationItems = this.querySelectorAll('.js-pagination__item');
          this.nextButton = this.querySelector('.js-pagination__next-button');

          // stop anything tied to old DOM
          this.intersectionObserver?.disconnect();
          clearInterval(this.nextButtonTimer);
          if (this.nextButton && this.clickHandler) {
            this.nextButton.removeEventListener('click', this.clickHandler);
          }

          // reset instance state (but DON'T lose already-hidden universals)
          this._universalBuffer = Array.from(this.querySelectorAll('.js-pagination__item.universal-buffered'));
          this._universalDone = false;
          this._lastFetchedHref = null;


          if (!this.paginationItems.length) return;

          // re-apply universal logic
          this.splitUniversalWithinContainer();
          this.pinInlinePromoToThird(); // ✅ pin after rerender split

          if (!this.hasNextPage()) {
            this.flushUniversalBuffer();
            this.pinInlinePromoToThird(); // ✅ pin after rerender flush
            return;
          }

          // rebind if pagination still exists
          this.bindListeners();
        }, 120);
      });

      // subtree:false prevents triggers from lazyloaded images/badges/etc.
      this._gridObserver.observe(target, { childList: true, subtree: false });
    }

    hasNextPage() {
      if (!this.paginationContainer) return false;
      if (!this.nextButton) return false;

      const href = this.nextButton.getAttribute('href');
      if (!href) return false;

      if (this.nextButton.hasAttribute('disabled')) return false;
      if (this.nextButton.getAttribute('aria-disabled') === 'true') return false;

      const resolved = new URL(href, window.location.href).toString();
      if (resolved === window.location.href) return false;

      return true;
    }

    destroy() {
      clearInterval(this.nextButtonTimer);
      clearTimeout(this._gridChangeTimer);

      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
      this._gridObserver?.disconnect();
    }

    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    bindListeners() {
      if (!this.nextButton) return;

      this.nextButton.removeEventListener('click', this.clickHandler);
      this.nextButton.addEventListener('click', this.clickHandler);

      if (this.dataset.infiniteScrollEnabled === 'true' && this.paginationContainer) {
        this.intersectionObserver?.disconnect();

        this.intersectionObserver = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.fetchNextItems();
                this.nextButtonTimer = setInterval(this.fetchNextItems.bind(this), 1500);
              } else {
                clearInterval(this.nextButtonTimer);
              }
            });
          },
          { rootMargin: `${window.innerHeight}px 0px` }
        );

        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    /**
     * Buffer universal items by hiding their wrapper <li> (faster than remove).
     */
    splitUniversalWithinContainer() {
      if (this._universalDone) return;
      // If we got reset but DOM still has hidden universals, rebuild the buffer
      if (!this._universalBuffer?.length) {
        this._universalBuffer = Array.from(this.querySelectorAll('.js-pagination__item.universal-buffered'));
      }


      const universalCards = this.querySelectorAll('.js-pagination__item [data-universal="true"]');

      universalCards.forEach(card => {
        const item = card.closest('.js-pagination__item');
        if (!item) return;

        if (item.classList.contains('universal-buffered')) return;

        this._universalBuffer.push(item);
        item.classList.add('universal-buffered'); // hide without removing
      });

      this.paginationItems = this.querySelectorAll('.js-pagination__item:not(.universal-buffered)');
    }

    /**
     * Append buffered universal items at the end once pagination ends.
     */
    flushUniversalBuffer() {
      if (this._universalDone) return;
      this._universalDone = true;

      if (!this._universalBuffer.length) return;

      const itemsContainer =
        this.querySelector('.js-pagination__item')?.parentElement ||
        this.querySelector('#product-grid') ||
        this.querySelector('.product-grid') ||
        this.querySelector('.js-pagination__items') ||
        this;

      const frag = document.createDocumentFragment();

      this._universalBuffer.forEach(item => {
        item.classList.remove('universal-buffered');
        frag.appendChild(item);
      });

      itemsContainer.appendChild(frag);

      this._universalBuffer = [];
      this.paginationItems = this.querySelectorAll('.js-pagination__item');

      this.pinInlinePromoToThird(); // ✅ pin after flush
    }

    async fetchNextItems() {
      if (this.fetchInProgress) return;

      const href = this.nextButton?.getAttribute('href') || this.nextButton?.href;
      if (!href) {
        this.cleanupAfterLastPage();
        return;
      }

      const resolvedHref = new URL(href, window.location.href).toString();
      if (this._lastFetchedHref === resolvedHref) {
        this.cleanupAfterLastPage();
        return;
      }
      this._lastFetchedHref = resolvedHref;

      const originalButtonText = this.nextButton?.textContent || '';
      this.fetchInProgress = true;

      if (this.nextButton) {
        this.nextButton.setAttribute('aria-disabled', 'true');
        this.nextButton.textContent =
          window?.strings?.general?.pagination?.loadingText || 'Loading…';
      }

      try {
        const response = await fetch(resolvedHref);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();

        const newItems = Array.from(template.content.querySelectorAll('.js-pagination__item'));
        const newItemsHTML = newItems.map(item => item.outerHTML).join('');

        if (!newItemsHTML) {
          this.cleanupAfterLastPage();
          return;
        }

        const lastIndex = this.paginationItems.length;

        this._suspendGridObserver = true;

        const insertAfterItem =
          this.paginationItems?.[this.paginationItems.length - 1] ||
          this.querySelector('.js-pagination__item:last-of-type');

        if (insertAfterItem) {
          insertAfterItem.insertAdjacentHTML('afterend', newItemsHTML);
        } else {
          const itemsContainer =
            this.querySelector('.js-pagination__item')?.parentElement ||
            this.querySelector('#product-grid') ||
            this.querySelector('.product-grid') ||
            this.querySelector('.js-pagination__items') ||
            this;
          itemsContainer.insertAdjacentHTML('beforeend', newItemsHTML);
        }

        this.paginationItems = this.querySelectorAll('.js-pagination__item');

        // Buffer universals from newly added items
        this.splitUniversalWithinContainer();
        this.pinInlinePromoToThird(); // ✅ pin after buffering

        setTimeout(() => {
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger();
          }
          window.renderSelects?.();
          window.loadDesktopOnlyTemplates?.();
          window.stickersReinit?.();

          if (
            document.body.classList.contains('tab-active') &&
            window.theme?.settings?.paginationLoadType === 'show_more'
          ) {
            this.paginationItems[lastIndex]?.querySelector('a')?.focus();
          }
        }, 300);

        this.updatePaginationControls(template);
      } catch (error) {
        console.error('Failed to load new items:', error);
      } finally {
        this.fetchInProgress = false;

        if (this.nextButton) {
          this.nextButton.removeAttribute('aria-disabled');
          this.nextButton.textContent = originalButtonText;
        }

        requestAnimationFrame(() => {
          this._suspendGridObserver = false;
        });
      }
    }

    updatePaginationControls(template) {
      const nextPagination = template.content.querySelector('.js-pagination');

      if (nextPagination && nextPagination.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshNextButton();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    refreshNextButton() {
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.nextButton?.addEventListener('click', this.clickHandler);
    }

    cleanupAfterLastPage() {
      this.flushUniversalBuffer();

      this.intersectionObserver?.disconnect();
      clearInterval(this.nextButtonTimer);

      this.paginationContainer?.remove();
      document.querySelectorAll('.js-when-paginated-only').forEach(elem => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}

document.addEventListener('DOMContentLoaded', () => {
  // If the element never upgraded, do a one-time reorder as a fallback
  document.querySelectorAll('infinite-scroll').forEach(el => {
    if (typeof el.splitUniversalWithinContainer === 'function') return;

    const items = [...el.querySelectorAll('.js-pagination__item')];
    if (!items.length) return;

    const container = items[0].parentElement;
    if (!container) return;

    const universals = items.filter(i => i.querySelector('[data-universal="true"]'));
    if (!universals.length) return;

    universals.forEach(i => container.appendChild(i));
    console.log('[InfiniteScroll fallback] moved universals:', universals.length, location.pathname);
  });
});
